import { defineComponent as _defineComponent } from 'vue'
import { useTHGCarStore } from '@/storenew/thg/cars';
    import { getCarStatusText, getProbPercent, getVehicleClassText } from '@/utils/thg';
    import { THGCar } from '@/interfaces/thg/THGCar';
    import { THGCarTableColumnVisibilityLabels } from '@/interfaces/thg/THGCarTableColumnVisibility';
    import { THGCarTableColumn } from '@/enums/thg/THGCarTableColumn';
    import { deleteTHGCar } from '@/api/thg/cars';
    import { THGPresignedUrl } from '@/interfaces/thg/THGPresignedUrl';
    import { alertError, confirm } from '@/utils/dialogs';
    import { THGCarPaginatedResponse } from '@/interfaces/thg/THGCarPaginatedResponse';
    import { storeToRefs } from 'pinia';
    import emitter from '@/utils/mitt';
    import { displayPrice, formatDate, vehicleRegistrationCert } from '@/utils/misc';
    import { useRoute, useRouter } from 'vue-router/composables';
    import { THGUser } from '@/interfaces/thg/THGUser';
    import { useTHGGlobalStore } from '@/storenew/thg/global';
    import { Country } from '@/enums/Country';
    import BoolIcon from '@/components/BoolIcon.vue';
    import { THGCarPayoffModel } from '@/enums/thg/THGCarPayoffModel';
    import { getPresignedUrl } from '@/api/thg/vrc';
    import { SortOrder } from '@/enums/misc';
    import { fetchTHGChangeHistoryItems } from '@/api/thg/history/change';
    import { ref } from 'vue';
    import THGChangeHistory from '@/components/thg/THGChangeHistory.vue';
    import { THGChangeHistoryItem } from '@/interfaces/thg/THGChangeHistoryItem';
    import { THGVehicleClass } from '@/enums/thg/THGVehicleClass';

    interface Props {
        data?: THGCarPaginatedResponse;
        user?: THGUser;
        isPaginated: boolean;
        showVRC: boolean;
        pageSize: number;
        currentPage?: number;
    }

    
export default /*#__PURE__*/_defineComponent({
  __name: 'GenericTHGCarTable',
  props: {
    data: null,
    user: null,
    isPaginated: { type: Boolean, default: false },
    showVRC: { type: Boolean, default: false },
    pageSize: { default: 20 },
    currentPage: { default: 1 }
  },
  emits: ["onPageChange", "dataReloading"],
  setup(__props: any, { emit }: { emit: ({
        (e: 'onPageChange', pageNum: number): void;
        (e: 'dataReloading'): void;
    }), expose: any, slots: any, attrs: any }) {

const props = __props as { data?: THGCarPaginatedResponse, user?: THGUser, isPaginated: boolean, showVRC: boolean, pageSize: number, currentPage: number };

    const { currentCountry } = storeToRefs(useTHGGlobalStore());
    const route = useRoute();
    const router = useRouter();

    

    

    const { columnVisibility } = storeToRefs(useTHGCarStore());

    const columnVisibilityLabels: THGCarTableColumnVisibilityLabels = {
        [THGCarTableColumn.UBA_SUBMITTED_DATE]: 'Einreichdatum',
        [THGCarTableColumn.DRIVE_CODE]: 'Antriebsart',
        [THGCarTableColumn.LICENSE_PLATE]: 'Kennzeichen',
        [THGCarTableColumn.INITIAL_REGISTRATION_DATE]: 'Erstzulassung',
        [THGCarTableColumn.DEREGISTRATION_DATE]: 'Abmeldedatum',
        [THGCarTableColumn.CONFIRMATION_DATE]: 'Bestätigungsdatum',
        [THGCarTableColumn.REGISTRATION_DATE]: 'Zulassung',
        [THGCarTableColumn.TERMS_VERSION]: 'AGB-Version',
        [THGCarTableColumn.FIRST_NAME]: 'Vorname',
        [THGCarTableColumn.NAME]: 'Name',
        [THGCarTableColumn.PROBABILITIES]: 'Wahrscheinlichkeiten',
        [THGCarTableColumn.PAYOFF_MODEL]: 'Prämienvariante',
    };

    /**
     * Check for existing field (used for rendering table columns).
     * @param name field name
     * @param row car object
     */
    function fieldExists(name: string, row: THGCar) {
        return name in row && row[name as keyof typeof row];
    }

    /**
     * Get the presigned url and open it in a new window.
     *
     * @param contract_file vrc file name
     */
    async function contractFileClicked(contract_file: string) {
        // wait for the presigned url to be generated and retrieved
        const urlResp = await getPresignedContractUrl(contract_file);

        // open a new window with url as resource
        window.open(urlResp.url, '_blank');
    }

    /**
     * Get the presigned url and open it in a new window.
     *
     * @param vrc_file vrc file name
     */
    async function vrcFileClicked(vrc_file: string) {
        // wait for the presigned url to be generated and retrieved
        const urlResp = await getPresignedVrcUrl(vrc_file);

        // open a new window with url as resource
        window.open(urlResp.url, '_blank');
    }

    /**
     * Wrapper to call the getPresignedUrl endpoint to retrieve contract file.
     *
     * @param fileKey
     * @return preSignedUrl response
     */
    async function getPresignedContractUrl(fileKey: string): Promise<THGPresignedUrl> {
        const res = await getPresignedUrl(fileKey, 'contracts');

        if (!res.parsedBody) {
            throw new Error('cannot retrieve contract presigned url');
        }

        return res.parsedBody;
    }

    /**
     * Wrapper to call the getPresignedUrl endpoint to retrieve VRC file.
     *
     * @param fileKey
     * @return preSignedUrl response
     */
    async function getPresignedVrcUrl(fileKey: string): Promise<THGPresignedUrl> {
        const res = await getPresignedUrl(fileKey);

        if (!res.parsedBody) {
            throw new Error('cannot retrieve vrc presigned url');
        }

        return res.parsedBody;
    }

    const onPageChange = (pageNum: number) => emit('onPageChange', pageNum);

    async function onSortChange(field: string, order: SortOrder) {
        const { sortData } = storeToRefs(useTHGCarStore());
        sortData.value = { field: field, order: order };

        emit('dataReloading');
    }

    async function deleteVehicle(vehicleObj: THGCar) {
        if (!vehicleObj._id) {
            throw new Error('Car ID is undefined.');
        }

        const question = `Soll das ausgewählte Fahrzeug mit der FIN ${vehicleObj.vin} wirklich gelöscht werden?`;
        const { result, dialog } = await confirm(
            'Fahrzeug löschen',
            question,
            'Löschen',
            'is-danger',
        );

        if (result) {
            // start loading
            emitter.emit('showLoader');

            // do the actual delete operation
            await deleteTHGCar(vehicleObj._id);

            // close dialog and re-fetch data
            dialog.close();

            // emit the dataReloading event and close the loader
            emit('dataReloading');
            emitter.emit('hideLoader');
        }
    }

    const showChangeHistory = ref<boolean>(false);
    const changeHistory = ref<THGChangeHistoryItem[]>();

    async function onHistoryClick(row: THGCar) {
        if (!row._id) {
            throw new Error('THG Car ID is undefined');
        }

        const historyResponse = await fetchTHGChangeHistoryItems(row._id);

        if (!historyResponse.parsedBody || !historyResponse.parsedBody.length) {
            alertError('Keine History', 'Für diese Fahrzeug gibt es keine Change-History.');
        } else {
            changeHistory.value = historyResponse.parsedBody;
            showChangeHistory.value = true;
        }
    }

    function onCarEdit(row: THGCar) {
        if (!route.name) {
            throw new Error('Route name is undefined');
        }

        if (!row._id) {
            throw new Error('Car ID is undefined.');
        }

        if (props.user) {
            router.push({
                name: 'thgcar',
                params: {
                    carId: row._id,
                    prevRouteName: route.name,
                    user: props.user as never as string,
                },
            });
        } else {
            router.push({
                name: 'thgcar',
                params: { carId: row._id, prevRouteName: route.name },
            });
        }
    }

    function onUserEdit(row: THGCar) {
        if (!route.name) {
            throw new Error('Route name is undefined');
        }

        if (!row.user_id) {
            throw new Error('User ID is undefined.');
        }

        router.push({
            name: 'thguser',
            params: { userId: row.user_id, prevRouteName: route.name },
        });
    }

return { __sfc: true,currentCountry, route, router, props, emit, columnVisibility, columnVisibilityLabels, fieldExists, contractFileClicked, vrcFileClicked, getPresignedContractUrl, getPresignedVrcUrl, onPageChange, onSortChange, deleteVehicle, showChangeHistory, changeHistory, onHistoryClick, onCarEdit, onUserEdit, getCarStatusText, getProbPercent, getVehicleClassText, THGCarTableColumn, displayPrice, formatDate, vehicleRegistrationCert, Country, BoolIcon, THGCarPayoffModel, THGChangeHistory, THGVehicleClass }
}

})